import type * as Sentry from '@sentry/react'

/**
 * PII scrubbing for Sentry events (OBS-9) — a `beforeSend` /
 * `beforeSendTransaction` belt-and-suspenders on top of Sentry's server-side
 * scrubbers and `sendDefaultPii: false`. Every tag/span/breadcrumb we add is a
 * new exfiltration surface; this makes those surfaces born-scrubbed.
 *
 * Two layers of defence:
 *   1. Deny-list by KEY (case-insensitive substring) — drops the whole value of
 *      any `extra`/`tag`/span-attribute/breadcrumb-data key that names a PII
 *      field (email, phone, name, address, message, detail, body, note, url,
 *      query, token, …).
 *   2. Redaction by VALUE — even under an innocuous key (`description`,
 *      `http.url`, an exception message), emails and phone numbers embedded in
 *      free text are replaced with `[Filtered]`. This is what catches the leak
 *      paths the key deny-list can't see: `event.message`, exception values,
 *      `event.request` URLs, and free-text values.
 *
 * Field policy:
 *   ✅ ALLOW  entity uuids (intervention_id, invoice_id, quote_id,
 *             technician_id), user.id, organization (uuid); enums (role,
 *             status, step, NotificationType, invoice status); route pattern,
 *             HTTP method, counts, durations, git SHA; Twilio/Brevo error
 *             code / HTTP status; PG error code/table/column/constraint.
 *   ❌ DENY   names, email, phone, address, geo; client business names,
 *             free-text amounts; raw request URL w/ query string,
 *             request/response bodies; Twilio/Brevo message bodies, recipient
 *             phone/email; PG error message/detail (echoes row PII);
 *             internalNote / any note body; secrets/tokens/API keys
 *             (x-api-key)/cookies.
 *
 * The scrubber fail-safes: any error while scrubbing a field drops that field
 * (or the string) rather than throwing — it must never crash the SDK send path.
 */

/**
 * Case-insensitive deny-list applied to keys as a SUBSTRING match, so `name`
 * covers firstName/lastName/first_name/client_name/technicianName/siteName,
 * `url` covers http.url, `query` covers query_string, and `key` covers the
 * technician credential header `x-api-key` (+ apiKey/api-key/secretKey/…). A
 * matched key has its entire value replaced with `[Filtered]`.
 */
const DENY_KEY_RE =
  /email|phone|name|address|message|detail|body|note|password|token|secret|key|authorization|cookie|url|query|target|statement/i

/** Emails anywhere in a free-text value. */
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi
/**
 * Phone numbers in free text: E.164-ish (`+` then 9–15 digits) or French
 * fixed/mobile (`+33`/`0` + 9 digits, any of space/dot/dash grouping). Kept
 * deliberately narrow so it does not eat entity ids, counts, or durations.
 */
const PHONE_RE = /\+\d{9,15}|(?:\+33|0)[\s.-]?[1-9](?:[\s.-]?\d{2}){4}/g

const FILTERED = '[Filtered]'
const MAX_DEPTH = 8

function isDenied(key: string): boolean {
  return DENY_KEY_RE.test(key)
}

/** Redact emails/phones embedded in a free-text string. Fail-safe → `[Filtered]`. */
function redactPii(value: string): string {
  try {
    return value.replace(EMAIL_RE, FILTERED).replace(PHONE_RE, FILTERED)
  } catch {
    return FILTERED
  }
}

/**
 * Recursively scrub an object/array: denied keys become `[Filtered]`, string
 * leaves are PII-redacted, all other values are recursed into. Returns a
 * scrubbed copy (never mutates the input). `seen` memoises scrubbed copies so a
 * shared reference (DAG) or a true cycle returns the SAME scrubbed copy — never
 * the raw, unscrubbed original. Per-key fail-safe: if scrubbing a value throws,
 * that key is dropped.
 */
function scrubValue(
  value: unknown,
  depth: number,
  seen: Map<object, unknown>
): unknown {
  if (value === null || typeof value !== 'object') {
    return typeof value === 'string' ? redactPii(value) : value
  }

  // Fail-safe: past the depth limit, drop the subtree rather than emit it raw.
  if (depth > MAX_DEPTH) return FILTERED

  const cached = seen.get(value)
  if (cached !== undefined) return cached

  if (Array.isArray(value)) {
    const out: unknown[] = []
    seen.set(value, out)
    for (const item of value) out.push(scrubValue(item, depth + 1, seen))
    return out
  }

  const out: Record<string, unknown> = {}
  seen.set(value, out)
  for (const key of Object.keys(value)) {
    try {
      out[key] = isDenied(key)
        ? FILTERED
        : scrubValue((value as Record<string, unknown>)[key], depth + 1, seen)
    } catch {
      // Fail-safe: drop the offending field rather than throw.
    }
  }
  return out
}

function scrubRecord<T>(record: T | undefined): T | undefined {
  if (!record || typeof record !== 'object') return record
  return scrubValue(record, 0, new Map()) as T
}

/**
 * Scrub a Sentry event in place (works for both error and transaction events).
 * Wired as `beforeSend` and `beforeSendTransaction`. Never throws — on any
 * internal error the event is returned best-effort rather than crashing send.
 */
export function scrubEventBeforeSend<T extends Sentry.Event>(event: T): T {
  try {
    // Structured payloads (deny-by-key + value redaction).
    if (event.extra) event.extra = scrubRecord(event.extra)
    if (event.tags) event.tags = scrubRecord(event.tags)
    if (event.user) event.user = scrubRecord(event.user)
    // ALL contexts (custom Sentry.setContext buckets, device.name, and the
    // trace context's span attributes under contexts.trace.data).
    if (event.contexts) event.contexts = scrubRecord(event.contexts)

    // Child-span attributes + the span's free-text `description` label (e.g.
    // an HTTP span `GET /search?query=<email>` or a SQL statement).
    if (Array.isArray(event.spans)) {
      for (const span of event.spans) {
        if (span?.data) span.data = scrubRecord(span.data) as typeof span.data
        if (typeof span?.description === 'string') {
          span.description = redactPii(span.description)
        }
      }
    }
    // Root transaction name (the root span's label may carry a query string).
    if (typeof event.transaction === 'string') {
      event.transaction = redactPii(event.transaction)
    }

    // Breadcrumbs: redact the message string, scrub the data payload.
    if (event.breadcrumbs) {
      for (const crumb of event.breadcrumbs) {
        if (typeof crumb?.message === 'string') {
          crumb.message = redactPii(crumb.message)
        }
        if (crumb?.data)
          crumb.data = scrubRecord(crumb.data) as typeof crumb.data
      }
    }

    // Free-text message + logentry.
    if (typeof event.message === 'string')
      event.message = redactPii(event.message)
    if (typeof event.logentry?.message === 'string') {
      event.logentry.message = redactPii(event.logentry.message)
    }

    // Exception messages — the most likely place PG/validation errors echo a
    // row value (e.g. `Key (email)=(...) already exists`).
    if (event.exception?.values) {
      for (const ex of event.exception.values) {
        if (typeof ex?.value === 'string') ex.value = redactPii(ex.value)
      }
    }

    // Request: drop the query string AND hash fragment, redact the path, scrub
    // body + headers, drop cookies. sendDefaultPii:false does NOT strip the URL.
    // The hash matters: Supabase carries access/refresh tokens in the fragment
    // (e.g. /reset-password#access_token=...), so `?`-only stripping leaks them.
    if (event.request) {
      const req = event.request
      if (typeof req.url === 'string')
        req.url = redactPii(req.url.split(/[?#]/)[0])
      delete (req as { query_string?: unknown }).query_string
      delete (req as { cookies?: unknown }).cookies
      // Request/response bodies are DENY policy — drop entirely, whatever the
      // shape. Key-scrubbing an object body is not enough: a body key like
      // `description` carries free-text (names/addresses) the value-redactor
      // can't catch. Safe ids/enums belong in `extra` at the call site (see
      // capture-error.ts), never in the raw body.
      if (req.data !== undefined && req.data !== null) req.data = FILTERED
      if (req.headers)
        req.headers = scrubRecord(req.headers) as typeof req.headers
    }
  } catch {
    // Never crash the SDK send path.
  }
  return event
}
